import { Link } from "react-router-dom";
import { useDaySummary } from "../hooks/useDaySummary";
import { useObservationsShadow } from "../hooks/useObservationsShadow";
import { useMyCorrectionRequests } from "../hooks/useCorrectionRequests";
import { needsWorkerAction } from "../lib/anomalyState";
import {
  formatClock,
  formatMinutes,
  plural,
  receivedDayPrefix,
} from "../lib/format";
import { DayFrise } from "./DayFrise";

/** Ligne discrète : le temps que les signaux du poste donnent pour la journée.
 * Rien tant que la journée n'est pas close, observée, et lisible. */
function ShadowLine({ workerUrl, day }: { workerUrl: string; day: string }) {
  const shadow = useObservationsShadow(workerUrl, day);
  const d = shadow.data?.days.find((x) => x.date === day);
  if (!d || d.partial || d.coverage === null) return null;
  return (
    <div className="dc-shadow">
      Selon les signaux du poste :{" "}
      {formatMinutes(Math.round(d.server_seconds / 60))}
    </div>
  );
}

/** Ma journée : la frise du jour par projet, le temps sans projet rattaché
 * sur place, provenance des pointages. Le titre est porté par l'écran
 * (aria-label ici). `editable` : la feuille du jour accepte la réimputation. */
export function YesterdayCard({
  workerUrl,
  day,
  label,
  editable,
}: {
  workerUrl: string;
  day: string;
  label: string;
  editable: boolean;
}) {
  const { isLoading, isError, recap, split, provenance, timeDay } =
    useDaySummary(workerUrl, day);
  const mine = useMyCorrectionRequests(!!workerUrl);
  const all = mine.data?.requests ?? [];
  // Toutes celles qui attendent le collaborateur, pas seulement celles de la
  // journée : une anomalie plus ancienne toujours à justifier reste visible.
  const toJustify = all.filter((a) => needsWorkerAction(a, all)).length;

  return (
    <section className="day-card" aria-label={label}>
      {isError ? (
        <div className="dc-empty">Pointages indisponibles</div>
      ) : isLoading ? (
        <div className="dc-empty">Chargement…</div>
      ) : !recap.hasEvents ? (
        <div className="dc-empty">Aucun pointage reçu pour cette journée</div>
      ) : (
        <>
          <DayFrise
            workerUrl={workerUrl}
            day={day}
            timeDay={timeDay}
            firstMin={recap.firstMin}
            lastMin={recap.lastMin}
            workedMin={recap.workedMin}
            unassignedMin={split.unassignedMin ?? 0}
            editable={editable}
          />

          <details className="dc-origin">
            <summary>D'où viennent ces heures</summary>
            <div className="dc-source">
              {provenance.kitReceivedAt
                ? `Remonté par le kit ${receivedDayPrefix(provenance.kitReceivedAt)}à ${formatClock(provenance.kitReceivedAt)}`
                : "Aucun pointage du kit"}
              {provenance.kitVersion && ` · kit ${provenance.kitVersion}`}
              {provenance.manualCount > 0 &&
                ` · ${provenance.manualCount} saisi${plural(provenance.manualCount)} à la main`}
            </div>
            <ShadowLine workerUrl={workerUrl} day={day} />
          </details>
        </>
      )}

      {toJustify > 0 && (
        <Link className="dc-anom" to="/anomalies">
          {toJustify} anomalie{plural(toJustify)} à justifier
        </Link>
      )}
    </section>
  );
}
